/**
 * Utility functions for image handling
 */

/**
 * Get image URL from various formats (URL, base64, File object)
 * @param {string|File} imageData - Image data in various formats
 * @returns {string|null} - Valid image URL or null
 */
export const getImageUrl = (imageData) => {
  if (!imageData) {
    return null;
  }
  
  // If it's a File object, create object URL for preview
  if (imageData instanceof File) {
    return URL.createObjectURL(imageData);
  }
  
  // Must be a string at this point
  if (typeof imageData !== 'string') {
    console.warn('getImageUrl: imageData is not a string or File:', typeof imageData);
    return null;
  }
  
  // Trim whitespace
  imageData = imageData.trim();
  
  // If it's a blob URL (blob:http://... or blob:https://...), return as is
  // Blob URLs are already valid and should be used directly
  if (imageData.startsWith('blob:')) {
    return imageData;
  }
  
  // If it's already a valid HTTP/HTTPS URL, return as is
  if (imageData.startsWith('http://') || imageData.startsWith('https://')) {
    return imageData;
  }
  
  // If it's a data URL, normalize it
  if (imageData.startsWith('data:')) {
    const normalized = normalizeBase64Image(imageData);
    if (!normalized) {
      console.warn('getImageUrl: Failed to normalize base64 image');
    }
    return normalized;
  }
  
  // If it's just base64 without prefix, add it
  // This handles cases where base64 is stored without data: prefix
  // But only if it doesn't look like a URL or blob
  if (imageData.length > 50 && 
      !imageData.includes(' ') && 
      !imageData.includes('/') && 
      !imageData.includes(':')) {
    // Likely base64, try to add prefix
    return normalizeBase64Image(`data:image/jpeg;base64,${imageData}`);
  }
  
  console.warn('getImageUrl: Unknown image format:', imageData.substring(0, 50));
  return null;
};

/**
 * Normalize base64 image data URL
 * Fixes common issues with base64 image data
 * @param {string} imageData - Base64 data URL
 * @returns {string|null} - Normalized data URL or null if invalid
 */
export const normalizeBase64Image = (imageData) => {
  if (!imageData || typeof imageData !== 'string') {
    return null;
  }
  
  // If it's already a valid image data URL, return as is
  if (imageData.startsWith('data:image/')) {
    return imageData;
  }
  
  // If it's base64 without data URL prefix, add it
  if (!imageData.startsWith('data:')) {
    return `data:image/jpeg;base64,${imageData}`;
  }
  
  // If it's a data URL with wrong MIME type (application/octet-stream), fix it
  if (imageData.startsWith('data:application/octet-stream')) {
    // Extract the base64 part
    const base64Part = imageData.includes(',') 
      ? imageData.split(',')[1] 
      : imageData.replace('data:application/octet-stream;base64,', '');
    
    // Validate base64 exists and has minimum length (even small images need at least 20 chars)
    if (!base64Part || base64Part.length < 20) {
      console.warn('normalizeBase64Image: Base64 muito curto ou vazio:', base64Part?.length || 0);
      return null; // Return null to show initials instead
    }
    
    // Try to detect actual image type from base64 header
    try {
      const detectedType = detectImageTypeFromBase64(base64Part);
      const normalized = `data:image/${detectedType};base64,${base64Part}`;
      console.log(`normalizeBase64Image: Normalized ${imageData.substring(0, 30)}... to ${detectedType}`);
      return normalized;
    } catch (e) {
      console.warn('normalizeBase64Image: Erro ao detectar tipo, usando JPEG:', e);
      // If detection fails, default to jpeg but still try to use it
      return `data:image/jpeg;base64,${base64Part}`;
    }
  }
  
  return imageData;
};

/**
 * Detect image type from base64 data
 * @param {string} base64Part - Base64 string (without data URL prefix)
 * @returns {string} - Image MIME type (jpeg, png, gif, webp)
 */
const detectImageTypeFromBase64 = (base64Part) => {
  try {
    // Decode first few bytes to detect format
    const header = base64Part.substring(0, 20);
    const binaryString = atob(header);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    // Check for image signatures
    // JPEG: FF D8 FF
    if (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) {
      return 'jpeg';
    }
    // PNG: 89 50 4E 47
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      return 'png';
    }
    // GIF: 47 49 46 38
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x38) {
      return 'gif';
    }
    // WebP: 52 49 46 46 (RIFF) followed by WEBP
    if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
      if (bytes.length >= 8 && 
          String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7]) === 'WEBP') {
        return 'webp';
      }
    }
    
    // Default to jpeg if we can't detect
    return 'jpeg';
  } catch (e) {
    // If detection fails, default to jpeg
    return 'jpeg';
  }
};

/**
 * Validate image file
 * @param {File} file - File object to validate
 * @returns {{valid: boolean, error?: string}} - Validation result
 */
export const validateImageFile = (file) => {
  if (!file) {
    return { valid: false, error: 'Nenhum arquivo selecionado' };
  }
  
  // Validate file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Por favor, selecione um arquivo de imagem válido' };
  }
  
  // Validate file size (max 2MB)
  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    return { valid: false, error: 'A imagem deve ter no máximo 2MB. Por favor, comprima a imagem antes de enviar' };
  }
  
  return { valid: true };
};

